import { gettingStartedActionPath } from "../../components/onboarding/onboardingRules";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./DataImportList.scss";
import { formatImportDate as formatDate } from "./importDate";
import {
  Button,
  Modal,
  ModalBody,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
} from "../../components/Common/AppOverlay";
import CustomDataGrid from "../../components/Common/CustomDataGrid";
import SummaryCards from "../../components/Common/SummaryCards";
import DataImportWizardPage from "../DataImport/DataImportWizardPage";
import {
  getDataImportReport,
  getDataImports,
} from "../../helpers/backend_helper";
import { ErrorNotify } from "../../shared/Alert";
import { showLoading } from "react-global-loading";

const getImportType = (item) =>
  item.dataImportType?.name || item.importType?.name || "Data import";

const getStatus = (item) =>
  item.importStatus?.name ||
  item.statusName ||
  (item.importStatusId === 1 ? "Queued" : "Unknown");

const getImportedBy = (item) =>
  item.importedBy ||
  item.createdUser?.fullName ||
  item.createdByName ||
  (item.createdBy ? `User #${item.createdBy}` : "—");

const getStatusTone = (status) => {
  if (/complete|success/i.test(status)) return "complete";
  if (/fail|error|reject/i.test(status)) return "failed";
  if (/process|progress/i.test(status)) return "processing";
  return "queued";
};

const parseImportLogData = (value) => {
  if (!value || typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const DataImportList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [imports, setImports] = useState([]);
  const [engagementImportOpen, setEngagementImportOpen] = useState(false);
  const [startedImport, setStartedImport] = useState(null);
  const [highlightedImportId, setHighlightedImportId] = useState(null);
  const [reportImport, setReportImport] = useState(null);
  const reportRequest = useRef(0);
  useEffect(
    () => () => {
      reportRequest.current += 1;
    },
    []
  );
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    type: "all",
  });
  const [quickFilter, setQuickFilter] = useState("all");

  const loadData = useCallback(async () => {
    showLoading(true);
    try {
      const response = await getDataImports();
      if (!response?.status) {
        setImports([]);
        ErrorNotify(response?.message || "Unable to load data imports.");
        return;
      }
      setImports(Array.isArray(response.data) ? response.data : []);
      window.dispatchEvent(new Event("piers:readiness-changed"));
    } catch (error) {
      console.error("Unable to load data imports", error);
      setImports([]);
      ErrorNotify("Unable to load data imports.");
    } finally {
      showLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    return () => showLoading(false);
  }, [loadData]);

  useEffect(() => {
    const importStarted = location.state?.importStarted;
    if (!importStarted) return undefined;

    let active = true;

    const showImportStarted = async () => {
      await loadData();
      if (!active) return;
      setFilters({ search: "", status: "all", type: "all" });
      setQuickFilter("all");
      setHighlightedImportId(importStarted.id || null);
      setStartedImport(importStarted);
      navigate(location.pathname, { replace: true, state: null });
    };
    showImportStarted();

    return () => {
      active = false;
    };
  }, [loadData, location.pathname, location.state, navigate]);

  useEffect(() => {
    if (!highlightedImportId) return undefined;
    const clearHighlight = window.setTimeout(
      () => setHighlightedImportId(null),
      6000
    );
    return () => window.clearTimeout(clearHighlight);
  }, [highlightedImportId]);

  const statusOptions = useMemo(
    () => [...new Set(imports.map(getStatus).filter(Boolean))],
    [imports]
  );

  const importTypeOptions = useMemo(
    () => [...new Set(imports.map(getImportType).filter(Boolean))],
    [imports]
  );

  const closeEngagementImport = () => {
    setEngagementImportOpen(false);
  };

  const showImportStarted = useCallback(
    async (importStarted) => {
      await loadData();
      setFilters({ search: "", status: "all", type: "all" });
      setQuickFilter("all");
      setHighlightedImportId(importStarted.id || null);
      setStartedImport(importStarted);
    },
    [loadData]
  );

  const startAnotherImport = () => {
    setStartedImport(null);
    setEngagementImportOpen(true);
  };

  const viewImportProgress = () => {
    const id = startedImport?.id;
    setStartedImport(null);
    if (id)
      openStatusReport(
        imports.find((item) => String(item.id) === String(id)) || { id }
      );
  };

  const closeStatusReport = () => {
    reportRequest.current += 1;
    setReportImport(null);
    setReportData(null);
    setReportLoading(false);
  };

  const openStatusReport = useCallback(async (item) => {
    const requestId = ++reportRequest.current;
    setReportImport(item);
    setReportData(null);
    setReportLoading(true);

    try {
      const response = await getDataImportReport(item.id);
      if (requestId !== reportRequest.current) return;
      if (!response?.status) {
        throw new Error(
          response?.message || "Unable to load the status report."
        );
      }
      setReportData(response.data);
      if (response.data?.completedOn)
        window.dispatchEvent(new Event("piers:readiness-changed"));
    } catch (error) {
      if (requestId !== reportRequest.current) return;
      console.error("Unable to load data import report", error);
      setReportData(null);
      ErrorNotify(error.message || "Unable to load the status report.");
    } finally {
      if (requestId === reportRequest.current) setReportLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!reportImport || !reportData || reportData.completedOn || reportLoading)
      return undefined;
    const timer = window.setTimeout(() => openStatusReport(reportImport), 8000);
    return () => window.clearTimeout(timer);
  }, [reportImport, reportData, reportLoading, openStatusReport]);

  const visibleImports = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    return imports.filter((item) => {
      const status = getStatus(item);
      const statusTone = getStatusTone(status);
      const matchesStatus =
        filters.status === "all" || status === filters.status;
      const matchesType =
        filters.type === "all" || getImportType(item) === filters.type;
      const matchesQuickFilter =
        quickFilter === "all" || statusTone === quickFilter;
      const matchesSearch =
        !search ||
        [
          getImportType(item),
          item.id,
          item.fileName,
          item.description,
          status,
          getImportedBy(item),
        ].some((value) =>
          String(value || "")
            .toLowerCase()
            .includes(search)
        );
      return (
        matchesStatus && matchesType && matchesQuickFilter && matchesSearch
      );
    });
  }, [filters, imports, quickFilter]);

  const summary = useMemo(() => {
    const completed = imports.filter(
      (item) => getStatusTone(getStatus(item)) === "complete"
    ).length;
    const processing = imports.filter(
      (item) => getStatusTone(getStatus(item)) === "processing"
    ).length;
    const queued = imports.filter(
      (item) => getStatusTone(getStatus(item)) === "queued"
    ).length;
    const failed = imports.filter(
      (item) => getStatusTone(getStatus(item)) === "failed"
    ).length;
    return { completed, processing, queued, failed };
  }, [imports]);

  const quickFilters = [
    ["all", "All Imports", imports.length, "bx-grid-alt"],
    ["processing", "In Progress", summary.processing, "bx-loader-alt"],
    ["queued", "Queued", summary.queued, "bx-time-five"],
    ["complete", "Completed", summary.completed, "bx-check-circle"],
    ["failed", "Failed", summary.failed, "bx-error-circle"],
  ];

  const columns = useMemo(
    () => [
      {
        id: "importJobNo",
        header: "Import Job No.",
        accessorKey: "id",
        size: 140,
        flex: 0.8,
        cell: ({ getValue }) => (
          <span className="data-import-grid__job">#{getValue() || "—"}</span>
        ),
      },
      {
        id: "importType",
        header: "Import Type",
        accessorFn: getImportType,
        size: 155,
        flex: 1,
        cell: ({ getValue }) => (
          <span className="data-import-grid__type">
            <i className="bx bx-spreadsheet" />
            {getValue()}
          </span>
        ),
      },
      {
        id: "fileName",
        header: "File Name",
        accessorKey: "fileName",
        size: 220,
        flex: 1.5,
        cell: ({ getValue }) => (
          <span className="data-import-grid__file">{getValue() || "—"}</span>
        ),
      },
      {
        id: "description",
        header: "Description",
        accessorKey: "description",
        size: 260,
        flex: 1.7,
        cell: ({ getValue }) => (
          <span className="data-import-grid__description">
            {getValue() || "No description"}
          </span>
        ),
      },
      {
        id: "submittedOn",
        header: "Submitted",
        accessorFn: (item) => formatDate(item.submittedOn || item.importDate || item.createdOn),
        size: 170,
        flex: 1,
      },
      {
        id: "importedBy",
        header: "Imported By",
        accessorFn: getImportedBy,
        size: 160,
        flex: 1,
      },
      {
        id: "completedOn",
        header: "Completed",
        accessorFn: (item) => formatDate(item.completedOn, true),
        size: 170,
        flex: 1,
      },
      {
        id: "status",
        header: "Status",
        accessorFn: getStatus,
        size: 130,
        flex: 0.8,
        cell: ({ row, getValue }) => (
          <span
            className={`data-import-status data-import-status--${getStatusTone(
              getStatus(row.original)
            )}`}
          >
            {getValue()}
          </span>
        ),
      },
      {
        id: "report",
        header: "Status Report",
        accessorKey: "id",
        disableColumnMenu: true,
        enableColumnFilter: false,
        enableSorting: false,
        flex: 1,
        size: 150,
        cell: ({ row }) => (
          <button
            className="data-import-grid__report-action"
            onClick={() => openStatusReport(row.original)}
            type="button"
          >
            <i className="bx bx-file-find" /> View report
          </button>
        ),
      },
    ],
    [openStatusReport]
  );

  const activeReport = reportData || reportImport;
  const reportLogs = reportData?.logs || [];

  return (
    <div className="page-content data-import-page piers-page">
      <header className="data-import-page__header piers-page-header">
        <div>
          <h2 className="piers-page-title">Data Import</h2>
          <p>Upload source files and track the progress of each data import.</p>
        </div>
        <div className="data-import-page__actions">
          <Button
            className="data-import-page__refresh piers-refresh-button"
            onClick={loadData}
            title="Refresh imports"
          >
            <i className="bx bx-refresh" /> Refresh
          </Button>
          <Button
            className="data-import-page__create"
            onClick={() => setEngagementImportOpen(true)}
          >
            <i className="bx bx-upload" /> Import Data
          </Button>
        </div>
      </header>

      <SummaryCards
        items={[
          [
            "bx-data",
            "blue",
            "Total Imports",
            imports.length,
            "All submitted files",
          ],
          [
            "bx-loader-alt",
            "orange",
            "In Progress",
            summary.processing,
            "Currently processing",
          ],
          [
            "bx-check-circle",
            "green",
            "Completed",
            summary.completed,
            "Successfully processed",
          ],
          [
            "bx-time-five",
            "purple",
            "Queued",
            summary.queued,
            "Awaiting processing",
          ],
        ]}
        ariaLabel="Data import summary"
      />

      

      <section
        className="data-import-quick-filters"
        aria-label="Data import quick filters"
      >
        {quickFilters.map(([value, label, count, icon]) => (
          <button
            className={quickFilter === value ? "active" : ""}
            key={value}
            onClick={() => setQuickFilter(value)}
            type="button"
          >
            <i className={`bx ${icon}`} /> {label} <b>{count}</b>
          </button>
        ))}
      </section>

      <section className="data-import-grid-card">
        <CustomDataGrid
 filterContent={<section
        className="data-import-filter-panel"
        aria-label="Data import filters"
      >
        <label className="data-import-filter-panel__search">
          <span>Search</span>
          <div>
            <i className="bx bx-search" />
            <input
              value={filters.search}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  search: event.target.value,
                }))
              }
              placeholder="Search imports..."
            />
          </div>
        </label>
        <label className="data-import-filter-panel__status">
          <span>Status</span>
          <select
            value={filters.status}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                status: event.target.value,
              }))
            }
          >
            <option value="all">All Statuses</option>
            {statusOptions.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </label>
        <label className="data-import-filter-panel__type">
          <span>Import Type</span>
          <select
            value={filters.type}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                type: event.target.value,
              }))
            }
          >
            <option value="all">All Import Types</option>
            {importTypeOptions.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="data-import-filter-panel__reset"
          onClick={() => {
            setFilters({ search: "", status: "all", type: "all" });
            setQuickFilter("all");
          }}
        >
          <i className="bx bx-reset" />Clear</button>
      </section>}

          data={visibleImports}
          columns={columns}
          onRefresh={loadData}
          fileName="data-imports"
          recordLabel="data imports"
          initialPageSize={15}
          showToolbar={false}
          rowHeight={56}
          columnHeaderHeight={56}
          gridSx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#ffffff !important",
              borderBottom: "1px solid #e7ebf2",
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader--sortable":
              { backgroundColor: "#ffffff !important", color: "#52627f" },
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "#52627f",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: ".2px",
            },
            "& .MuiDataGrid-columnSeparator": { color: "#e7ebf2" },
            "& .MuiDataGrid-cell": {
              borderBottomColor: "#edf0f5",
              color: "#273957",
              fontSize: 13,
            },
            "& .MuiDataGrid-row:hover": { backgroundColor: "#f8faff" },
            "& .MuiDataGrid-row.data-import-grid__row--highlighted": {
              animation: "data-import-row-highlight 6s ease-out forwards",
              backgroundColor: "#e6f0ff",
            },
            "& .MuiDataGrid-footerContainer": {
              borderTopColor: "#e7ebf2",
              fontSize: 14,
            },
            "& .MuiDataGrid-footerContainer .MuiTypography-root": {
              fontSize: 14,
            },
          }}
          getRowClassName={(params) =>
            String(params.row.__original?.id) === String(highlightedImportId)
              ? "data-import-grid__row--highlighted"
              : ""
          }
        />
      </section>

      <Offcanvas
        backdrop
        className="data-import-report-drawer"
        direction="end"
        isOpen={Boolean(reportImport)}
        toggle={closeStatusReport}
      >
        <OffcanvasHeader toggle={closeStatusReport}>
          <div>
            <h3>Import Status Report</h3>
            <p>Import job #{activeReport?.id || reportImport?.id}</p>
          </div>
        </OffcanvasHeader>
        <OffcanvasBody>
          <section className="data-import-report-drawer__summary">
            <span>Status</span>
            <strong
              className={`data-import-status data-import-status--${getStatusTone(
                activeReport?.status || getStatus(reportImport || {})
              )}`}
            >
              {activeReport?.status || getStatus(reportImport || {})}
            </strong>
            <dl>
              <div>
                <dt>File</dt>
                <dd>{activeReport?.fileName || "—"}</dd>
              </div>
              <div>
                <dt>Submitted</dt>
                <dd>
                  {formatDate(
                    activeReport?.submittedOn || activeReport?.importDate
                  )}
                </dd>
              </div>
              <div>
                <dt>Completed</dt>
                <dd>{formatDate(activeReport?.completedOn, true)}</dd>
              </div>
            </dl>
          </section>

          {!reportLoading && reportData?.completedOn && (
            <section className="data-import-report-next" aria-live="polite">
              <h4>
                {reportData.createdCount > 0
                  ? `${reportData.createdCount} records created`
                  : reportData.createdCount === 0
                  ? "No records were created"
                  : "Import finished"}
              </h4>
              <p>
                {reportData.failedCount > 0
                  ? `${reportData.failedCount} records failed. Review the details below before retrying those records.`
                  : reportData.createdCount > 0
                  ? "Your records are saved. Continue with the next step when you are ready."
                  : "Review the processing details below for the result."}
              </p>
              {gettingStartedActionPath(reportData.nextAction) && (
                <Button
                  color="primary"
                  onClick={() => {
                    window.dispatchEvent(new Event("piers:readiness-changed"));
                    navigate(gettingStartedActionPath(reportData.nextAction));
                  }}
                >
                  {reportData.nextAction.label} →
                </Button>
              )}
              <Button outline onClick={closeStatusReport}>
                I’ll do this later
              </Button>
            </section>
          )}
          {!reportLoading && reportData && !reportData.completedOn && (
            <p role="status">
              Your import is still processing.{" "}
              <button
                className="btn btn-link piers-refresh-button"
                onClick={() => openStatusReport(reportImport)}
              >
                Refresh status
              </button>
            </p>
          )}
          <section className="data-import-report-drawer__results">
            <h4>Processing details</h4>
            {reportLoading && (
              <div className="data-import-report-drawer__empty">
                <i className="bx bx-loader-alt bx-spin" /> Loading status
                report…
              </div>
            )}
            {!reportLoading && reportData && reportLogs.length === 0 && (
              <div className="data-import-report-drawer__empty">
                <i className="bx bx-info-circle" />
                <p>
                  Detailed row results are not available for this import. Check
                  the existing records before deciding whether to retry any
                  rows.
                </p>
              </div>
            )}
            {!reportLoading && !reportData && (
              <div className="data-import-report-drawer__empty">
                <i className="bx bx-error-circle" />
                <p>The status report could not be loaded. Please try again.</p>
              </div>
            )}
            {!reportLoading &&
              reportLogs.map((log) => {
                const rowData = parseImportLogData(log.dataJSON);
                const issues = Array.isArray(rowData?.Issues)
                  ? rowData.Issues
                  : [];
                return (
                  <article
                    className={`data-import-report-drawer__result data-import-report-drawer__result--${getStatusTone(
                      log.dataStatus || ""
                    )}`}
                    key={log.id}
                  >
                    <div>
                      <strong>{log.targetAction || "Import processing"}</strong>
                      <span>{log.dataStatus || "Information"}</span>
                    </div>
                    {(rowData?.Sheet || rowData?.Row) && (
                      <small>
                        {rowData?.Sheet ? `${rowData.Sheet} · ` : ""}
                        {rowData?.Row ? `Excel row ${rowData.Row}` : ""}
                      </small>
                    )}
                    <p>
                      {log.dataNote ||
                        log.note ||
                        "No additional detail provided."}
                    </p>
                    {issues.length > 0 && (
                      <ul>
                        {issues.map((issue) => (
                          <li key={issue}>{issue}</li>
                        ))}
                      </ul>
                    )}
                  </article>
                );
              })}
          </section>
        </OffcanvasBody>
      </Offcanvas>

      <Modal
        isOpen={engagementImportOpen}
        modalClassName="engagement-import-modal"
        onClosed={loadData}
        toggle={closeEngagementImport}
      >
        <ModalBody className="p-0">
          {engagementImportOpen && (
            <DataImportWizardPage
              closeAction={closeEngagementImport}
              onImportStarted={showImportStarted}
              showImportTypeSelection
            />
          )}
        </ModalBody>
      </Modal>

      <Modal
        centered
        isOpen={Boolean(startedImport)}
        modalClassName="data-import-started-modal"
        toggle={() => setStartedImport(null)}
      >
        <ModalBody className="data-import-started-modal__body">
          <button
            aria-label="Close import started confirmation"
            className="data-import-started-modal__close"
            onClick={() => setStartedImport(null)}
            type="button"
          >
            <i className="bx bx-x" />
          </button>
          <div className="data-import-started-modal__success-icon">
            <i className="bx bx-check" />
          </div>
          <h2>Import Started</h2>
          <p className="data-import-started-modal__message">
            Your{" "}
            {startedImport?.importType === "engagement"
              ? "engagement"
              : "client"}{" "}
            import has been submitted and is now being processed.
          </p>
          <div className="data-import-started-modal__metrics">
            <article>
              <strong>{startedImport?.ready || 0}</strong>
              <span>Ready</span>
            </article>
            <article>
              <strong>{startedImport?.warnings || 0}</strong>
              <span>Warnings</span>
            </article>
            <article>
              <strong>{startedImport?.excluded || 0}</strong>
              <span>Excluded</span>
            </article>
          </div>
          <p className="data-import-started-modal__supporting-copy">
            You can continue working in PIERS. We&apos;ll notify you when the
            import is complete.
          </p>
          <div className="data-import-started-modal__actions">
            <Button outline onClick={startAnotherImport}>
              Import Another File
            </Button>
            <Button color="primary" onClick={viewImportProgress}>
              View Import Progress
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default DataImportList;
